// ============================================================
// Tie-In — main.jsx
// React entry point.
// Reads Supabase session, determines userId + role,
// renders TriageApp with auth context.
// ============================================================

import { StrictMode, useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { createClient } from '@supabase/supabase-js';
import TriageApp from './modules/triage/TriageApp';
import { t } from './lib/triageTheme';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

function Root() {
  const [session, setSession] = useState(null);
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchUserRecord(session.user.id);
      else setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) fetchUserRecord(session.user.id);
      else { setUser(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchUserRecord(authId) {
    const { data } = await supabase
      .from('users')
      .select('id, name, email, role, tenant_id')
      .eq('id', authId)
      .single();
    setUser(data);
    setLoading(false);
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100dvh', background: t.colors.bg }}>
        <div style={{
          width: '36px', height: '36px',
          border: `3px solid ${t.colors.border}`,
          borderTop: `3px solid ${t.colors.accent}`,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!session || !user) {
    return <LoginScreen />;
  }

  return (
    <TriageApp
      userId={user.id}
      userRole={user.role}
      tenantId={user.tenant_id}
    />
  );
}

function LoginScreen() {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  async function handleLogin() {
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setError(error.message); setLoading(false); }
  }

  return (
    <div style={{
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      height:         '100dvh',
      background:     t.colors.bg,
      padding:        '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '360px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '32px', fontWeight: 700, fontFamily: t.fonts.display,
            color: t.colors.accent, letterSpacing: '0.05em', marginBottom: '4px' }}>
            TIE-IN
          </div>
          <div style={{ fontSize: '13px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>
            Email Triage · Mint Roofing
          </div>
        </div>

        {error && (
          <div style={{ padding: '10px 14px', background: t.colors.dangerDim, border: `1px solid ${t.colors.danger}44`,
            borderRadius: t.radii.md, color: t.colors.danger, fontSize: '13px', fontFamily: t.fonts.body,
            marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: '12px' }}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            style={{
              width: '100%', padding: '13px 14px',
              background: t.colors.bgPanel, border: `1px solid ${t.colors.border}`,
              borderRadius: t.radii.lg, color: t.colors.textPrimary,
              fontFamily: t.fonts.body, fontSize: '14px', outline: 'none',
            }}
          />
        </div>
        <div style={{ marginBottom: '20px' }}>
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            style={{
              width: '100%', padding: '13px 14px',
              background: t.colors.bgPanel, border: `1px solid ${t.colors.border}`,
              borderRadius: t.radii.lg, color: t.colors.textPrimary,
              fontFamily: t.fonts.body, fontSize: '14px', outline: 'none',
            }}
          />
        </div>
        <button
          onClick={handleLogin}
          disabled={loading || !email || !password}
          style={{
            width: '100%', padding: '14px',
            background: email && password ? t.colors.accent : t.colors.bgPanel,
            border: `1px solid ${email && password ? t.colors.accent : t.colors.border}`,
            borderRadius: t.radii.lg, color: email && password ? '#fff' : t.colors.textMuted,
            fontSize: '15px', fontWeight: 500, fontFamily: t.fonts.body,
            cursor: email && password ? 'pointer' : 'not-allowed',
            transition: t.transitions.fast,
          }}
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <Root />
  </StrictMode>
);
